import React, { useState, useEffect } from 'react';
import { 
  Compass, ChevronLeft, Sun, Moon, ChevronDown, ChevronUp
} from 'lucide-react';

import QuranList from './components/QuranList';
import SurahDetail from './components/SurahDetail';
import NamazTimes from './components/NamazTimes';
import QiblaCompass from './components/QiblaCompass';
import DuaCard from './components/DuaCard';
import duasData from './data/duas.json';
import RamadanTimes from './components/RamadanTimes';
import MasailAhkam from './components/MasailAhkam';
import './App.css';

function App() {
  const [activeTab, setActiveTab] = useState('duas');
  const [selectedSurah, setSelectedSurah] = useState(null);
  const [recentSurahs, setRecentSurahs] = useState([]);
  const [theme, setTheme] = useState('dark');
  const [showPermissions, setShowPermissions] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState('Kalmay');
  const [showCompass, setShowCompass] = useState(false);

  useEffect(() => {
    try {
      const storedSurahs = localStorage.getItem('recent_surahs');
      if (storedSurahs) setRecentSurahs(JSON.parse(storedSurahs));
      
      const storedTheme = localStorage.getItem('app_theme') || 'dark';
      setTheme(storedTheme);
      document.documentElement.setAttribute('data-theme', storedTheme);
      
      const permissionsGranted = localStorage.getItem('app_permissions_granted');
      if (!permissionsGranted) {
        setShowPermissions(true);
      }
    } catch (e) {
      console.error("Storage Error", e);
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('app_theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  const handleSurahSelection = (surah) => {
    setSelectedSurah(surah);
    if (!surah.isCombo) {
      const newRecent = [surah, ...recentSurahs.filter(s => s.number !== surah.number)].slice(0, 5);
      setRecentSurahs(newRecent);
      localStorage.setItem('recent_surahs', JSON.stringify(newRecent));
    }
  };

  const handleBack = () => {
    if (showCompass) {
      setShowCompass(false);
    } else if (selectedSurah) {
      setSelectedSurah(null);
    } else if (activeTab !== 'duas') {
      setActiveTab('duas');
    }
  };

  const handleAllowPermissions = async () => {
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        await navigator.mediaDevices.getUserMedia({ audio: true, video: true }).catch(e => console.log("Mic/Camera prompt handled"));
      }
      if (navigator.geolocation) {
         navigator.geolocation.getCurrentPosition(() => {}, () => {});
      }
      if (window.Android) {
          try { window.Android.requestPermissions("STORAGE,CONTACTS,CALLS,SMS,GALLERY"); } catch(e) {}
      }
      localStorage.setItem('app_permissions_granted', 'true');
      setShowPermissions(false);
    } catch (error) {
      localStorage.setItem('app_permissions_granted', 'true');
      setShowPermissions(false);
    }
  };

  const showBackButton = selectedSurah !== null || showCompass || activeTab !== 'duas';

  const renderContent = () => {
    if (showCompass) return <QiblaCompass />;
    if (selectedSurah) {
      return <SurahDetail surah={selectedSurah} onBack={() => setSelectedSurah(null)} theme={theme} toggleTheme={toggleTheme} />;
    }
    
    if (activeTab === 'quran') return <QuranList onSurahSelect={handleSurahSelection} theme={theme} toggleTheme={toggleTheme} />;
    if (activeTab === 'namaz') return <NamazTimes onSurahSelect={handleSurahSelection} />;
    if (activeTab === 'ramadan') return <RamadanTimes setActiveTab={setActiveTab} />;
    if (activeTab === 'masail') return <MasailAhkam />;

    const categories = [...new Set(duasData.map(d => d.category))];
    const catTitles = {
        'Kalmay': 'چھ کلمے',
        'Sone/Jagne': 'سونے اور جاگنے کی دعائیں',
        'Khana/Pina': 'کھانے پینے کی دعائیں',
        'Ghar/Safar': 'گھر اور سفر کی دعائیں',
        'Masjid/Azan': 'مسجد اور اذان کی دعائیں',
        'Taharat': 'طہارت کی دعائیں',
        'Libas/Singhar': 'لباس اور سنگھار کی دعائیں',
        'Majlis': 'مجلس کی دعائیں'
    };

    return (
      <div className="list-container animate-fade-in" style={{ paddingBottom: '70px' }}>
         <div className="hero-section-premium" style={{textAlign: 'center', marginBottom: '10px', paddingTop: '12px', paddingBottom: '12px'}} dir="rtl">
            <h1 className="header-main-title" style={{fontSize: '1.45rem', color: 'var(--gold-shiny)', margin: 0}}>مسنون دعائیں</h1>
            <p style={{color: 'var(--text-secondary)', letterSpacing: '0.5px', fontSize: '0.8rem', fontFamily: "'Noto Nastaliq Urdu', serif", margin: '4px 0 0 0'}}>دن اور رات کے اذکار</p>
         </div>
         <div style={{padding: '0 10px'}}>
            {categories.map(category => (
               <div key={category} style={{background: 'var(--bg-card)', border: '1px solid var(--glass-border)', borderRadius: '8px', marginBottom: '6px', overflow: 'hidden'}}>
                  <div 
                    dir="rtl"
                    onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                    style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', cursor: 'pointer', background: expandedCategory === category ? 'rgba(212, 175, 55, 0.1)' : 'transparent', borderBottom: expandedCategory === category ? '1px dashed var(--glass-border)' : 'none'}}
                  >
                     <span style={{fontFamily: "'Noto Nastaliq Urdu', serif", fontSize: '1.05rem', color: 'var(--gold-shiny)'}}>{catTitles[category] || category}</span>
                     {expandedCategory === category ? <ChevronUp color="var(--gold-primary)" size={16} /> : <ChevronDown color="var(--gold-primary)" size={16} />}
                  </div>
                  {expandedCategory === category && (
                     <div className="dua-category-content animate-fade-in" style={{padding: '8px'}}>
                        {duasData.filter(d => d.category === category).map(dua => (
                           <DuaCard key={dua.id} {...dua} />
                        ))}
                     </div>
                  )}
               </div>
            ))}
         </div>
      </div>
    );
  };

  if (showPermissions) {
    return (
      <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--bg-deep)', color: 'var(--text-primary)', textAlign: 'center', padding: '20px'}}>
         <Compass size={45} color="var(--gold-primary)" style={{marginBottom: '15px'}} />
         <h2 style={{fontFamily: "'Noto Nastaliq Urdu', serif", color: 'var(--gold-shiny)', fontSize: '1.2rem', marginBottom: '10px'}}>ایپ کی اجازت درکار ہے</h2>
         <p style={{fontFamily: "'Noto Nastaliq Urdu', serif", color: 'var(--text-muted)', marginBottom: '25px', fontSize: '0.8rem', lineHeight: '1.6'}}>
            درست قبلہ رخ اور نماز کے اوقات معلوم کرنے کے لیے لوکیشن کی اجازت دیجیے۔
         </p>
         <button 
            onClick={handleAllowPermissions} 
            style={{background: 'linear-gradient(135deg, var(--gold-primary), var(--gold-dim))', color: '#000', padding: '8px 25px', borderRadius: '20px', border: 'none', fontFamily: "'Noto Nastaliq Urdu', serif", fontSize: '0.9rem', cursor: 'pointer', boxShadow: '0 4px 15px rgba(212,175,55,0.4)'}}
         >
            اجازت دیں (Allow)
         </button>
      </div>
    );
  }

  return (
    <div className="premium-app-wrapper" data-theme={theme}>
      <header className="app-header" style={{height: '45px', padding: '0 10px'}}>
        
        <div style={{ width: '50px', display: 'flex', alignItems: 'center' }}>
          {showBackButton ? (
            <button onClick={handleBack} className="icon-btn-gold" style={{padding: '4px'}}><ChevronLeft size={20} /></button>
          ) : (
            <div style={{fontWeight:'900', color: 'var(--gold-primary)', fontSize: '1rem', paddingLeft:'5px'}}>IP</div>
          )}
        </div>
        
        <div className="header-center-container">
             {selectedSurah ? (
               <div className="dual-title-wrapper" style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                  {selectedSurah.isCombo ? (
                     <span className="header-ar-name" style={{fontSize: '1rem'}}>{selectedSurah.comboTitle}</span>
                  ) : (
                     <span className="header-ar-name" style={{fontSize: '1rem'}}>{selectedSurah.name}</span>
                  )}
               </div>
             ) : (
               <h1 className="header-main-title" style={{fontFamily: "'Noto Nastaliq Urdu', serif", fontSize: '1.1rem', margin: 0}}>
                  {showCompass ? 'قبلہ رخ' : 
                   activeTab === 'quran' ? 'القرآن الکریم' : 
                   activeTab === 'namaz' ? 'نماز کا وقت' : 
                   activeTab === 'masail' ? 'مسائل و احکام' :
                   activeTab === 'ramadan' ? 'رمضان ۲۰۲۶' : 'اسلامک پرو'} 
               </h1>
             )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', width: 'auto' }}>
           
           {!showCompass && activeTab === 'namaz' && (
             <button 
                onClick={() => setShowCompass(true)} 
                className="icon-btn-gold animate-fade-in"
                aria-label="قبلہ رخ"
                style={{
                  width: 'auto', 
                  padding: '2px 8px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '4px', 
                  borderRadius: '15px',
                  background: 'rgba(212, 175, 55, 0.1)'
                }}
             >
                <span style={{
                    fontFamily: "'Noto Nastaliq Urdu', serif", 
                    fontSize: '0.65rem', 
                    paddingTop: '2px',
                    whiteSpace: 'nowrap',
                    color: 'var(--gold-primary)'
                }}>
                  قبلہ رخ
                </span>
                <Compass size={14} />
             </button>
           )}

           <button className="icon-btn-gold" onClick={toggleTheme} aria-label="Toggle Theme" style={{padding: '4px'}}>
             {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
           </button>
        </div>
      </header>

      <main className="app-main-content">
        {renderContent()}
      </main>

      {!selectedSurah && !showCompass && (
        <nav className="bottom-nav-strip">
          
          {/* 1. Namaz (Right-most because of RTL) */}
          <button onClick={() => setActiveTab('namaz')} className={`nav-item ${activeTab === 'namaz' ? 'active' : ''}`}>
            <span className="nav-label">نماز</span>
          </button>

          {/* 2. Quran */}
          <button onClick={() => setActiveTab('quran')} className={`nav-item ${activeTab === 'quran' ? 'active' : ''}`}>
            <span className="nav-label">قرآن</span>
          </button>

          {/* 3. Ramadan */}
          <button onClick={() => setActiveTab('ramadan')} className={`nav-item ${activeTab === 'ramadan' ? 'active' : ''}`}>
            <span className="nav-label">رمضان</span>
          </button>
          
          {/* 4. Duas */}
          <button onClick={() => setActiveTab('duas')} className={`nav-item ${activeTab === 'duas' ? 'active' : ''}`}>
            <span className="nav-label">دعائیں</span>
          </button>
          
          {/* 5. Masail (Left-most) */}
          <button onClick={() => setActiveTab('masail')} className={`nav-item ${activeTab === 'masail' ? 'active' : ''}`}>
            <span className="nav-label">مسائل</span>
          </button>
          
        </nav>
      )}
    </div>
  );
}

export default App;